<!--

Slots:
------

#default
обёрнут в fieldset, меняющий состояние disabled

#unlocked
снаружи fieldset, смена состояния редактирования не влияет

#pre-controls
внутри нижнего блока кнопок, перед первой общей кнопокй, не учитывает состояние формы (редактирование вкл/выкл)

#controls
внутри нижнего блока кнопок, после общих кнопок; виден только в режиме редактирования при наличии прав (canEdit + entityRbac)

#post-controls
внутри нижнего блока кнопок, после блока #controls; виден при наличии прав (canEdit + entityRbac), без учёта режима редактирования

#sticky-banner
при stickyControls — плашка/алерт в стики-панели (внизу экрана при скролле)

#sticky-end
только в стики-панели, справа в той же строке. В потоке документа не показывается.

 -->

<template>
  <DfnControlTpl>
    <slot name="pre-controls"></slot>
    <template v-if="entity.id && canEdit && havePermissions && isVisible && !isEntityDeleted">
      <a
        href="#"
        class="btn btn-secondary"
        @click.prevent="edit"
        v-if="!isEditing && !isLockedByOthers && canEdit"
      >
        <i class="bi bi-pencil"></i>
        {{ editLabelText }}
      </a>
      <a
        href="#"
        @click.prevent="onClickReset"
        v-if="isEditing"
        class="btn btn-secondary"
      >
        <i class="bi bi-x"></i>
        {{ t('exitEdit') }}
      </a>

          <button
            class="btn btn-outline-secondary"
            type="button"
            @click.prevent="rollback"
            v-if="rollbackAvail"
            :disabled="rollbackPending"
          >
            <i class="bi bi-arrow-counterclockwise"></i>
            {{ t('rollback') }}
          </button>
          <a
            href="#"
            @click.prevent="reset"
            v-if="isLockedByOthers && canResetLock"
            class="btn btn-danger"
          >
            <i class="bi bi-unlock"></i>
            {{ t('resetLock') }}
          </a>
        </template>
        <template v-if="isEditing && havePermissions && canEdit && !isEntityDeleted">
          <button
            class="btn btn-primary"
            type="button"
            :disabled="isDocumentUploading"
            @click.prevent="onSubmit"
            v-if="canSave"
          >
            <span
              v-if="isDocumentUploading"
              class="spinner-border spinner-border-sm"
              role="status"
              aria-hidden="true"
            ></span>
            <i v-else class="bi bi-check2"></i>
            {{ t('common.save') }}
          </button>
        </template>

    <slot
      name="controls"
      v-if="isEditing && havePermissions && canEdit && !isEntityDeleted"
    ></slot>
    <slot
      name="post-controls"
      v-if="havePermissions && canEdit"
    ></slot>
  </DfnControlTpl>

  <form
    @submit.stop.capture.prevent="onSubmit"
    ref="me"
    class="form-blockable"
    :class="entityName ? `form-blockable--${entityName}` : null"
  >
    <BlockScreen v-if="blockScreen" />
    <div
      v-if="isLoadingEntity"
      class="text-center py-5"
    >
      <i class="bi bi-arrow-clockwise spinning"></i>
      <div class="mt-2">{{ t('common.loading') }}</div>
    </div>
    <template v-else>
      <template v-if="lockManual">
        <slot
          :editable="isFormEditable"
          :errors="errors"
        />
      </template>
      <template v-else>
        <fieldset :disabled="!isFormEditable">
          <slot
            :editable="isFormEditable"
            :errors="errors"
          />
        </fieldset>
      </template>

      <slot name="unlocked"></slot>
      <div
        v-if="!hideControls"
        class="hstack mt-3 gap-3 align-items-center flex-wrap submissions-form-controls"
      >
        <template v-if="remoteControls">
          <Teleport
            :to="remoteControlsTarget"
            :disabled="!remoteControlsTarget"
          >
            <div class="hstack gap-3 align-items-center flex-wrap submissions-form-controls__remote">
              <div
                class="alert alert-info mb-0"
                v-if="isLockedByOthers"
              >
                <i class="bi bi-lock"></i>
                {{ t('lockedByOther', { user: lockAuthorName }) }}
              </div>
              <div
                class="alert alert-info mb-0"
                v-if="!havePermissions"
              >
                {{ t('noPermissions') }}
              </div>
              <slot name="sticky-banner"></slot>
              <ReControlTpl />
              <slot
                name="create-act-pp"
                v-if="!isEditing"
              ></slot>
            </div>
          </Teleport>
        </template>
        <template v-else-if="stickyControls">
          <Stickypanel :also-hide-when-visible-ref="stickyHideWhenVisibleRef">
            <template #static>
              <div
                class="alert alert-info mb-0"
                v-if="isLockedByOthers"
              >
                <i class="bi bi-lock"></i>
                {{ t('lockedByOther', { user: lockAuthorName }) }}
              </div>
              <div
                class="alert alert-info mb-0"
                v-if="!havePermissions"
              >
                {{ t('noPermissions') }}
              </div>
              <slot name="sticky-banner"></slot>
              <ReControlTpl />
              <slot
                name="create-act-pp"
                v-if="!isEditing"
              ></slot>
            </template>
            <template #sticky>
              <div
                class="alert alert-info mb-0"
                v-if="isLockedByOthers"
              >
                <i class="bi bi-lock"></i>
                {{ t('lockedByOther', { user: lockAuthorName }) }}
              </div>
              <div
                class="alert alert-info mb-0"
                v-if="!havePermissions"
              >
                {{ t('noPermissions') }}
              </div>
              <slot name="sticky-banner"></slot>
              <ReControlTpl />
              <slot
                name="create-act-pp"
                v-if="!isEditing"
              ></slot>
              <div
                v-if="$slots['sticky-end']"
                class="sticky-panel__end"
              >
                <slot name="sticky-end"></slot>
              </div>
            </template>
          </Stickypanel>
        </template>
        <template v-else>
          <div
            class="alert alert-info"
            v-if="isLockedByOthers"
          >
            <i class="bi bi-lock"></i>
            {{ t('lockedByOther', { user: lockAuthorName }) }}
          </div>
          <div
            class="alert alert-info"
            v-if="!havePermissions"
          >
            {{ t('noPermissions') }}
          </div>
          <ReControlTpl />
          <slot
            name="create-act-pp"
            v-if="!isEditing"
          ></slot>
        </template>
      </div>
    </template>
  </form>
</template>
<script setup>
import {
  onUnmounted,
  watch,
  provide,
  computed,
  onMounted,
  nextTick,
  reactive,
  toRaw,
  toValue,
  shallowRef,
} from 'vue'
import { storeToRefs } from 'pinia'
import { onBeforeRouteLeave } from 'vue-router'
import { camelCase } from 'lodash-es'
import { useI18n } from 'vue-i18n'
import { useIntervalFn } from '@vueuse/core'
import config from '@/config'
import { useApi } from 'soykajs/composables/api'
import { mapApiErrorsToFields, resolveApiErrorMessage } from '@/lib/apiErrors'
import { useLockStore } from '@/stores/lock'
import { useUserStore } from '@/stores/user'
import { useUiStore } from '@/stores/ui'
import { provideDocumentUploadPending } from '@/composables/documentUploadPending'
import {
  clearPendingUploadRemoves,
  flushPendingUploadRemoves,
} from '@/composables/pendingUploadRemoves'
import {
  consumeEditLeaveConfirmSkipped,
  confirmExitEdit,
  isDocViewerRoute,
  stashEditSession,
} from '@/composables/ui'
import { getBilingualPairMissingFields } from '@/lib/bilingualFields'
import { useCardCollapse } from '@/composables/useCardCollapse'
import { scrollToFirstInvalidField } from '@/lib/dom'
import BlockScreen from '@/components/blockscreen.vue'
import Stickypanel from '@/components/stickypanel.vue'
import { createReusableTemplate } from '@vueuse/core'

const [DfnControlTpl, ReControlTpl] = createReusableTemplate()

const { t } = useI18n()

const props = defineProps({
  entityName: {
    type: String,
    required: true,
  },
  entityRbac: {
    type: Array,
    required: true,
  },
  entity: {
    type: Object,
    required: true,
  },
  // edit button eabled (true)/disabled(false)
  canEdit: {
    type: Boolean,
    default: true,
  },
  editLabel: {
    type: String,
    required: false,
  },
  isVisible: {
    type: Boolean,
    default: true,
  },
  canSave: {
    type: Boolean,
    default: true,
  },
  rules: Array,
  lockManual: {
    type: Boolean,
    default: false,
  },
  /**
   * Без серверного lock (User и др. с `__no_locks__`): CRUD в SSO,
   * локальной строки в backend может не быть — GET /lock иначе даёт 404.
   */
  noLock: {
    type: Boolean,
    default: false,
  },
  noDraft: Boolean,
  stickyControls: {
    type: Boolean,
    default: true,
  },
  /** Телепорт кнопок формы в общий контейнер (submission stages). */
  remoteControls: {
    type: Boolean,
    default: false,
  },
  remoteControlsTarget: {
    type: String,
    default: '',
  },
  /** Скрыть нижний блок кнопок (когда controlHost на другом этапе). */
  hideControls: {
    type: Boolean,
    default: false,
  },
  /** Ref на элемент в основном контенте: при его видимости стики-панель скрывается */
  stickyHideWhenVisibleRef: {
    type: Object,
    default: null,
  },
})
const emits = defineEmits([
  'form:reset',
  'form:save',
  'form:apply',
  'form:editable',
  'form:update',
  'form:autosave',
])
let poll

const me = shallowRef(null)
const cardCollapse = useCardCollapse(me)

const userStore = useUserStore()
const { user } = storeToRefs(userStore)
const { lockKeys } = storeToRefs(useLockStore())
const { blockScreen } = storeToRefs(useUiStore())
const { isDocumentUploading } = provideDocumentUploadPending()

const draftEnabled = computed(() => {
  if (config.VITE_DRAFT_ENABLED !== 'true') {
    return false
  }

  return !props.noDraft
})
const lockStatePollInterval = Number(config.VITE_LOCK_INTERVAL ?? 5000)
const lockStateQuery = computed(() => ({ entity_name: props.entityName, id: props.entity.id }))
const entityPath = computed(() =>
  props.entity?.id ? `${props.entityName}/${props.entity.id}` : null
)

const modelName = computed(() => {
  let name = camelCase(props.entityName)
  name = name[0].toUpperCase() + name.slice(1)
  return name
})

const { model: lockState, refresh: fetchLockState } = useApi({
  url: 'lock_check',
  query: lockStateQuery,
  before: () => !props.noLock && Boolean(props.entity?.id),
  // Не abort'ить предыдущий poll: иначе ensureLock/lockPoll получают null
  // и решают по устаревшему lockState → ложный «Entity locked by another user».
  abortPrevious: false,
})
const isLoadingEntity = computed(() => {
  return (
    !props.entity?._loaded && typeof props.entity?.id != 'undefined' && !isNaN(props.entity?.id)
  )
})
const { refresh: lockSet, model: lockItem } = useApi({
  url: 'lock',
  query: lockStateQuery,
  before: () => !props.noLock && Boolean(props.entity?.id),
  abortPrevious: false,
})
const { refresh: lockReset } = useApi({
  url: 'lock_reset',
  query: lockStateQuery,
  before: () => !props.noLock && Boolean(props.entity?.id),
  abortPrevious: false,
})

/** Локальный ключ режима редактирования при noLock (без X-Api-Lock на сервере). */
const NO_LOCK_KEY = '__no_lock__'
const { model: snapshotList, refresh: fetchSnapshots } = useApi({
  url: computed(() => `snapshot/${toValue(modelName)}/${props.entity.id}`),
  before: () => draftEnabled.value && Boolean(modelName.value && props.entity.id),
  watch: [() => props.modelName, () => props.entity.id],
  data: {},
})
const { refresh: rollbackSnapshot, pending: rollbackPending } = useApi({
  url: computed(() => `snapshot/${toValue(modelName)}/${props.entity.id}/rollback`),
})

const { model: draft, refresh: fetchDraft } = useApi({
  url: computed(() => `draft/${modelName.value}/${props.entity.id}`),
  immediate: true,
  before: () => {
    if (!props.entity?.id) {
      return false
    }
    return draftEnabled.value
  },
})
const lk = computed({
  get() {
    return lockKeys.value[entityPath.value] ?? null
  },
  set(val) {
    if (entityPath.value === null) {
      return
    }
    const keys = { ...lockKeys.value }
    if (val === undefined) {
      delete keys[entityPath.value]
    } else {
      keys[entityPath.value] = val
    }

    lockKeys.value = keys
  },
})
/** false после reset/forgetLock — игнор позднего ответа lock(), иначе снова включается edit. */
let acceptLockResponses = true
const isLockNone = computed(() =>
  lockState.value ? lockState.value.success === true && lockState.value.lock === null : null
)
const canResetLock = computed(() => userStore.can('locks_reset'))
const isLockedByMe = computed(() => {
  if (props.noLock) {
    return lk.value != null
  }
  return lockState.value?.success === true && lockState.value.lock?.lock_key === lk.value
})

function isLockAuthorCurrentUser(lock) {
  const author = lock?.author
  if (!author || !user.value) {
    return false
  }
  if (author.id != null && user.value.id != null && String(author.id) === String(user.value.id)) {
    return true
  }
  const authorName = author.username
  const myName = user.value.username
  return Boolean(authorName && myName && String(authorName) === String(myName))
}

/** Чужой lock: свой «осиротевший» (без локального ключа) не считаем чужим. */
const isLockedByOthers = computed(() => {
  const ls = lockState.value
  if (!ls || ls.success !== false || !ls.lock) {
    return false
  }
  if (isLockAuthorCurrentUser(ls.lock)) {
    return false
  }
  return ls.lock.lock_key !== lk.value
})

function adoptLockKey(lock) {
  const key = lock?.lock_key
  if (key) {
    lk.value = key
  }
}
const isEditing = computed(() => {
  return props.entity?.id ? lk.value !== null : true
})
const isEntityDeleted = computed(() => {
  const value = props.entity?.deleted
  return value === true || value === 1 || value === '1' || value === 'true' || value === 'True'
})
const rollbackAvail = shallowRef(false)

const havePermissions = computed(
  () => userStore.can(props.entityRbac)
)
/** Редактирование UI только при правах; иначе view-only даже при lock-manual / stale lock. */
const isFormEditable = computed(
  () => Boolean(props.canEdit) && havePermissions.value && isEditing.value && !isEntityDeleted.value
)
const editLabelText = computed(() => props.editLabel ?? t('common.edit'))
const lockAuthorName = computed(
  () => lockState.value?.lock?.author?.fio || lockState.value?.lock?.author?.username
)
const validationErrors = reactive({})
const apiErrors = reactive({})
let submitScrollPosition = null

/**
 * Собирает ошибки формы и переводит backend-сообщения реактивно.
 *
 * Перевод выполняется здесь, а не при получении ответа API, чтобы смена locale обновляла уже показанную ошибку.
 */
const errors = computed(() => {
  const result = { ...validationErrors }
  Object.entries(apiErrors).forEach(([key, options]) => {
    result[key] = resolveApiErrorMessage(options)
  })
  return result
})

provide('form:editable', isFormEditable)

watch(isLockNone, (val, prev) => {
  // refresh all form instance after someone edited model
  if (prev === false && val === true) {
    emits('form:reset')
  }
})

watch(
  () => props.entity?.id,
  async id => {
    if (!id) {
      lockPollStop()
      return
    }
    if (!props.noLock) {
      await lockPoll()
      lockPollStart()
    }
    await fetchSnapshots()
  },
  { immediate: true }
)

watch(
  lockState,
  ls => {
    if (!ls) {
      return
    }
    // Свободно — локальный ключ не нужен.
    if (ls.success === true && ls.lock === null) {
      lk.value = undefined
      return
    }
    // Наш lock с валидным ключом — синхронизируем, только если уже редактируем
    // (иначе poll «осиротевшего» lock не открывал бы edit сам).
    if (ls.success === true && ls.lock?.lock_key) {
      if (lk.value && (ls.lock.lock_key === lk.value || isLockAuthorCurrentUser(ls.lock))) {
        adoptLockKey(ls.lock)
      }
      return
    }
    // success:false без заголовка: свой lock не трогаем (ensureLock подхватит ключ).
    // Чужой — сбрасываем локальный ключ.
    if (ls.success === false && ls.lock) {
      if (isLockAuthorCurrentUser(ls.lock)) {
        return
      }
      lk.value = undefined
    }
  },
  // useApi делает Object.assign в тот же объект — без deep watch не видит смены success/lock.
  { immediate: true, deep: true }
)
watch(
  lockItem,
  l => {
    if (!l || !acceptLockResponses) {
      return
    }
    if (l.success === true) {
      adoptLockKey(l.lock)
      return
    }
    // lock(): тот же пользователь — бэкенд продлевает и отдаёт ключ (success true).
    // success:false здесь только чужой lock.
    if (l.success === false && l.lock && !isLockAuthorCurrentUser(l.lock)) {
      lk.value = undefined
    }
  },
  { deep: true }
)

watch(
  isFormEditable,
  async editable => {
    emits('form:editable', editable)
    await fetchSnapshots()
  },
  { immediate: true }
)

watch(
  snapshotList,
  snapshotList => {
    rollbackAvail.value = Boolean(snapshotList)
  },
  { deep: true }
)

watch(blockScreen, visible => {
  if (!visible) {
    restoreSubmitScrollPosition()
  }
})

onBeforeRouteLeave(beforeRouteChanges)

/** Уход в R7: Form размонтируется, но lock в store оставляем до возврата на карточку. */
let preserveLockOnUnmount = false

onMounted(async () => {
  window.addEventListener('modalstack:close', unBlockScreen)
  window.addEventListener('beforeunload', unload)

  if (draftEnabled.value) {
    await applyDraft()
  }

  await nextTick()
  cardCollapse.start()
})

onUnmounted(async () => {
  cardCollapse.stop()

  if (preserveLockOnUnmount) {
    lockPollStop()
  } else if (isLockedByMe.value) {
    await fullReset()
  } else {
    lockPollStop()
  }

  window.removeEventListener('beforeunload', unload)
  window.removeEventListener('modalstack:close', unBlockScreen)
})

if (Number(config.VITE_FORM_AUTOSAVE_INTERVAL) > 0 && user.value.autosave) {
  useIntervalFn(async () => {
    if (!isEditing.value) {
      return
    }

    emits('form:autosave')
    await fetchSnapshots()
  }, config.VITE_FORM_AUTOSAVE_INTERVAL)
}

/**
 * Guard ухода с карточки: confirm + снятие lock, кроме перехода в R7.
 *
 * @param {object} to целевой маршрут.
 * @returns {Promise<boolean>} разрешить навигацию.
 */
async function beforeRouteChanges(to) {
  if (!isLockedByMe.value) {
    lockPollStop()
    return true
  }

  // Карандаш в просмотрщике → /docview → /edit: не выходим из редактирования карточки.
  if (isDocViewerRoute(to)) {
    preserveLockOnUnmount = true
    lockPollStop()
    // Карточка размонтируется — сохраняем локальные правки до возврата.
    if (entityPath.value && props.entity) {
      stashEditSession(entityPath.value, toRaw(props.entity))
    }
    return true
  }

  if (consumeEditLeaveConfirmSkipped(entityPath.value)) {
    lockPollStop()
    await fullReset()
    return true
  }

  if (!(await confirmExitEdit())) {
    return false
  }

  lockPollStop()
  if (isLockedByMe.value) {
    await fullReset()
  }
}

function lockPollStart() {
  lockPollStop()
  if (props.noLock || lockStatePollInterval === -1) {
    return
  }
  poll = setInterval(lockPoll, lockStatePollInterval)
}

function lockPollStop() {
  if (poll === undefined) {
    return
  }

  clearInterval(poll)
  poll = undefined
}

async function lockPoll() {
  if (props.noLock) {
    return lockState.value
  }
  let fls = {}
  if (lk.value) {
    fls.headers = {
      'X-Api-Lock': lk.value,
    }
  }
  return fetchLockState(fls)
}

function unBlockScreen() {
  blockScreen.value = false
}

async function reset() {
  acceptLockResponses = false
  if (!props.noLock) {
    try {
      await lockReset(lockResetOptions())
    } catch {
      /* lock мог истечь за время R7 — всё равно выходим из редактирования */
    }
  }
  lk.value = undefined
  if (!props.noLock) {
    try {
      await lockPoll()
    } catch {
      /* ignore */
    }
  }
}

async function onClickReset() {
  if (!isEditing.value) {
    return
  }

  if (!(await confirmExitEdit())) {
    return
  }

  if (props.entity?.id) {
    await reset()
    lockPollStart()
    if (draftEnabled.value) {
      await fetchDraft({ method: 'DELETE' })
    }
  } else {
    forgetLock()
  }

  clearPendingUploadRemoves()
  emits('form:reset')
}

async function lockAcquire() {
  acceptLockResponses = true
  if (props.noLock) {
    lk.value = NO_LOCK_KEY
    return
  }
  // useApi.refresh() → Promise<void>; ответ только в lockItem
  await lockSet()
  let result = lockItem.value
  if (result == null) {
    await lockSet()
    result = lockItem.value
  }
  if (result?.success === true && result?.lock) {
    adoptLockKey(result.lock)
  } else if (
    result?.success === false &&
    result?.lock &&
    isLockAuthorCurrentUser(result.lock)
  ) {
    adoptLockKey(result.lock)
  }
  await lockPoll()
}

async function edit() {
  if (!props.canEdit || isEntityDeleted.value) {
    return
  }
  acceptLockResponses = true
  await lockAcquire()
}

/**
 * Убедиться, что lock взят (lock-manual): при отсутствии или протухании — захватить заново.
 * На время захвата останавливаем interval-poll, чтобы не гонять параллельные lock_check.
 */
async function ensureLock() {
  if (!props.entity?.id) {
    return
  }
  if (props.noLock) {
    if (!lk.value) {
      lk.value = NO_LOCK_KEY
    }
    return
  }
  lockPollStop()
  try {
    let state = await lockPoll()
    // null = устаревший/abort'нутый ответ — один повтор.
    if (state == null) {
      state = await lockPoll()
    }
    if (lk.value && isLockedByMe.value) {
      return
    }
    // Свой lock на сервере без локального ключа (или success:false без заголовка).
    if (lockState.value?.lock && isLockAuthorCurrentUser(lockState.value.lock)) {
      adoptLockKey(lockState.value.lock)
      if (lk.value) {
        return
      }
    }
    await lockAcquire()
    if (!lk.value) {
      throw new Error('Entity locked by another user')
    }
  } finally {
    if (props.entity?.id) {
      lockPollStart()
    }
  }
}

async function fullReset() {
  if (!props.noLock) {
    try {
      await lockReset(lockResetOptions())
    } catch {
      /* lock мог истечь — локальный ключ всё равно снимаем */
    }
  }
  forgetLock()
}

function lockResetOptions() {
  if (!lk.value) {
    return {}
  }
  return {
    headers: {
      'X-Api-Lock': lk.value,
    },
  }
}

function forgetLock() {
  acceptLockResponses = false
  lk.value = undefined
  lockPollStop()
}

async function unload() {
  if (isLockedByMe.value) {
    await fullReset()
  }
}

function isArrayItemsRule(rule) {
  return rule.type === 'array' && Array.isArray(rule.items)
}

function isBilingualPairRule(rule) {
  return rule.type === 'bilingualPair' && Array.isArray(rule.fields)
}

function validationValue(rule, value) {
  if (rule.type === 'object') {
    return value?.id
  }
  if (rule.type === 'string' && typeof value === 'string') {
    return value.trim()
  }
  return value
}

/**
 * Проверяет required для одного поля и пишет ошибку в переданный ключ.
 * Для `type: 'object'` обязательным считается наличие `id`, а не сам объект.
 */
function validateRuleValue(rule, value, errorKey) {
  const checkValue = validationValue(rule, value)

  if (rule.required && !checkValue) {
    validationErrors[errorKey] = rule.message
    return false
  }

  return true
}

function bilingualPairRuleMessage(rule) {
  return rule.message || t('pairedFieldRequired')
}

/**
 * Проверяет правило двуязычной пары и пишет ошибку только в незаполненную сторону.
 *
 * @param {object} rule Правило с `fields: [first, second]`.
 * @param {object} source Объект, где лежит пара полей.
 * @param {string} pathPrefix Префикс для ошибок вложенных массивов.
 * @returns {boolean} true, если пара пустая или заполнена полностью.
 */
function validateBilingualPairRule(rule, source, pathPrefix) {
  const missingFields = getBilingualPairMissingFields(source, rule.fields)
  if (!missingFields.length) {
    return true
  }

  missingFields.forEach(field => {
    const key = pathPrefix ? `${pathPrefix}.${field}` : field
    if (!validationErrors[key]) {
      validationErrors[key] = bilingualPairRuleMessage(rule)
    }
  })
  return false
}

/**
 * Валидирует правило и его вложенные массивы рекурсивно.
 * Form хранит ошибки плоскими ключами (`afs_apis.0.manufacturers.0.field`),
 * чтобы дочерние компоненты могли показать сообщение рядом с конкретным input.
 */
function validateRuleNode(rule, value, path) {
  if (isArrayItemsRule(rule)) {
    if (!Array.isArray(value)) {
      return true
    }

    let valid = true
    value.forEach((item, index) => {
      rule.items.forEach(itemRule => {
        const childPath = isBilingualPairRule(itemRule)
          ? `${path}.${index}`
          : `${path}.${index}.${itemRule.name}`
        const childValue = isBilingualPairRule(itemRule) ? item : item?.[itemRule.name]
        if (!validateRuleNode(itemRule, childValue, childPath)) {
          valid = false
        }
      })
    })

    return valid
  }

  if (isBilingualPairRule(rule)) {
    return validateBilingualPairRule(rule, value, path)
  }

  return validateRuleValue(rule, value, path)
}

function clearRuleErrors(rule) {
  if (isArrayItemsRule(rule)) {
    const prefix = `${rule.name}.`
    // Массивы могут удалять и переиндексировать элементы, поэтому чистим весь namespace правила.
    Object.keys(validationErrors).forEach(key => {
      if (key.startsWith(prefix) && validationErrors[key]) {
        validationErrors[key] = null
        delete apiErrors[key]
      }
    })
    Object.keys(apiErrors).forEach(key => {
      if (key.startsWith(prefix)) {
        delete apiErrors[key]
      }
    })
    return
  }

  if (isBilingualPairRule(rule)) {
    const message = bilingualPairRuleMessage(rule)
    rule.fields.forEach(field => {
      if (validationErrors[field] === message) {
        validationErrors[field] = null
      }
    })
    return
  }

  if (validationErrors[rule.name]) {
    validationErrors[rule.name] = null
  }
  delete apiErrors[rule.name]
}

function validate() {
  let valid = true
  Object.keys(validationErrors).forEach(key => {
    validationErrors[key] = null
  })
  Object.keys(apiErrors).forEach(key => delete apiErrors[key])

  props.rules.forEach(rule => {
    if (isArrayItemsRule(rule)) {
      // Для массивов проверяем каждую карточку и пишем ошибки в ключи `array.index.field`.
      if (!validateRuleNode(rule, props.entity[rule.name], rule.name)) {
        valid = false
      }
      return
    }

    if (isBilingualPairRule(rule)) {
      if (!validateRuleNode(rule, props.entity, '')) {
        valid = false
      }
      return
    }

    if (!validateRuleValue(rule, props.entity[rule.name], rule.name)) {
      valid = false
    }
  })

  return valid
}

function collectOrderedErrorKeys() {
  const result = []
  if (!props.rules?.length) {
    return result
  }

  function appendArrayRule(rule) {
    const items = props.entity[rule.name]
    if (!Array.isArray(items)) {
      return
    }

    items.forEach((item, index) => {
      rule.items.forEach(itemRule => {
        if (isArrayItemsRule(itemRule)) {
          const nested = item?.[itemRule.name]
          if (!Array.isArray(nested)) {
            return
          }
          nested.forEach((_, nestedIndex) => {
            itemRule.items.forEach(nestedRule => {
              const path = `${rule.name}.${index}.${itemRule.name}.${nestedIndex}.${nestedRule.name}`
              if (errors.value[path]) {
                result.push(path)
              }
            })
          })
          return
        }

        const path = `${rule.name}.${index}.${itemRule.name}`
        if (errors.value[path]) {
          result.push(path)
        }
      })
    })
  }

  for (const rule of props.rules) {
    if (isArrayItemsRule(rule)) {
      appendArrayRule(rule)
      continue
    }
    if (errors.value[rule.name]) {
      result.push(rule.name)
    }
  }

  return result
}

async function scrollAfterValidation() {
  blockScreen.value = false
  const errorKeys = collectOrderedErrorKeys()
  await nextTick()
  await new Promise(resolve => requestAnimationFrame(resolve))
  scrollToFirstInvalidField(me.value, { errorKeys })
}

function rememberSubmitScrollPosition() {
  submitScrollPosition = {
    x: window.scrollX,
    y: window.scrollY,
  }
}

async function restoreSubmitScrollPosition() {
  if (!submitScrollPosition) {
    return
  }
  const pos = submitScrollPosition
  submitScrollPosition = null
  await nextTick()
  await new Promise(resolve => requestAnimationFrame(resolve))
  window.scrollTo(pos.x, pos.y)
}

async function emitApply() {
  await flushPendingUploadRemoves()
  emits('form:apply')
}

async function onSubmit() {
  if (!props.canSave || blockScreen.value || isDocumentUploading.value) {
    return
  }
  blockScreen.value = true
  try {
    if (props.lockManual && props.entity?.id) {
      await ensureLock()
    }
    if (props.rules) {
      if (validate()) {
        rememberSubmitScrollPosition()
        await emitApply()
      } else {
        await scrollAfterValidation()
      }
    } else {
      rememberSubmitScrollPosition()
      await emitApply()
    }
  } catch (err) {
    submitScrollPosition = null
    blockScreen.value = false
    throw err
  }
  // Успешный save: blockScreen снимает родитель в onSave.finally.
  // Если apply синхронно не стартовал — не оставляем вечный overlay.
  if (!props.rules || !Object.values(validationErrors).some(Boolean)) {
    /* parent finally */
  }
}
if (props.rules) {
  props.rules.forEach(rule => {
    watch(
      () => isBilingualPairRule(rule)
        ? rule.fields.map(field => props.entity[field])
        : props.entity[rule.name],
      () => {
        clearRuleErrors(rule)
      },
      // Для вложенных карточек изменение происходит внутри элемента массива, а не только заменой массива.
      isArrayItemsRule(rule) ? { deep: true } : undefined
    )
  })
}
watch(
  () => props.rules,
  () => {
    // Новые правила появляются, например, при смене locale; повторяем проверку,
    // только если в форме уже показана клиентская ошибка.
    if (Object.values(validationErrors).some(Boolean)) {
      const currentApiErrors = { ...apiErrors }
      validate()
      Object.assign(apiErrors, currentApiErrors)
    }
  }
)

async function rollback() {
  if (!snapshotList.value) {
    return
  }

  blockScreen.value = true

  try {
    await rollbackSnapshot()

    // backend answers 204 if no snaps,
    // should trigger snapshotlist by hand
    snapshotList.value = undefined
    await fetchSnapshots()

    clearPendingUploadRemoves()
    emits('form:reset')
  } finally {
    blockScreen.value = false
  }
}

async function applyDraft() {
  blockScreen.value = true
  try {
    await fetchDraft()

    if (draft.value) {
      emits('form:update', draft.value?.data_after)
      await reset()
      edit()
    }
  } finally {
    blockScreen.value = false
  }
}

/**
 * Подставляет field-ошибки из ApiError.errors (пункты с loc) во внутренний errors.
 *
 * @param {object} err ApiError или совместимый объект с .errors.
 * @returns {boolean} true, если хотя бы одно поле выставлено.
 */
function applyApiErrors(err) {
  const fieldMap = mapApiErrorsToFields(err)
  if (!fieldMap) {
    return false
  }
  Object.keys(fieldMap).forEach(key => {
    apiErrors[key] = fieldMap[key]
    const stop = watch(
      () => key.split('.').reduce((cur, part) => cur?.[part], props.entity),
      () => {
        delete apiErrors[key]
        validationErrors[key] = null
        stop()
      }
    )
  })
  return true
}

defineExpose({
  reset,
  edit,
  ensureLock,
  forgetLock,
  isLockedByOthers,
  canResetLock,
  applyApiErrors,
  submit: onSubmit,
  isEditing,
  canEdit: computed(() => props.canEdit && havePermissions.value),
})
</script>

<style>
.btn-icon-link {
  --bs-btn-border-width: 0;
  --bs-btn-padding-x: 0;

  display: inline-flex;
  align-items: center;
  gap: 1rem;
}

.btn-icon-link:hover {
  text-decoration: underline;
}

.btn-icon-link .bi {
  width: 16px;
  height: 16px;
  font-size: 16px;
  line-height: 1;
}

.form-control-row-action::before {
  content: "";
  display: block;
  height: calc(var(--bs-body-line-height, 1.5) * 1em);
  margin-bottom: .5rem;
}
</style>

<i18n>
{
  "ru": {
    "exitEdit": "Выйти из редактирования",
    "rollback": "Вернуть",
    "resetLock": "Сбросить блокировку",
    "lockedByOther": "Редактирование невозможно: пользователь {user} уже редактирует эту запись",
    "noPermissions": "Редактирование невозможно. У вас нет прав",
    "edit": "Редактировать",
    "pairedFieldRequired": "Заполните парное двуязычное поле"
  },
  "en": {
    "exitEdit": "Exit editing",
    "rollback": "Rollback",
    "resetLock": "Reset lock",
    "lockedByOther": "Editing is not available: user {user} is already editing this record",
    "noPermissions": "Editing is not available. You do not have permission",
    "edit": "Edit",
    "pairedFieldRequired": "Fill in the paired bilingual field"
  }
}
</i18n>
